// Lib
import React, { useEffect } from 'react';
import { compose } from '../../../node_module_clones/recompose';
import { isEmpty } from 'lodash';

// Components
import ElementDropTarget from '../dnd/elementDropTargets/ElementDropTarget';

// Utils
import { isAnnotation, isLine } from '../../../common/elements/utils/elementTypeUtils';
import { getElementId, getPhysicalId } from '../../../common/elements/utils/elementPropertyUtils';
import type { MNElement, MNElementLocation } from '../../../common/elements/elementModelTypes';
import type { ElementType } from '../../../common/elements/elementTypes';

// Drop Utils
import inboxDropLocationFn from '../../inbox/inboxDropLocationFn';
import canvasDropLocationFn from '../dnd/canvasDropLocationFn';
import makeCanDropFn from '../dnd/canDrop/makeCanDropFn';
import preventAllElementPredicate from '../dnd/canDrop/preventAllElementPredicate';
import validElementPermissionsPredicate from '../dnd/canDrop/validElementPermissionsPredicate';
import navigateOnHoverDecorator from '../dnd/navigateOnHoverDecorator';
import onlyShowIfNotPresentational from '../dnd/elementDropTargets/onlyShowIfNotPresentational';
import preventSnappedDragDrop from '../dnd/canDrop/preventSnappedDragDrop';

// Constants
import { DROP_TARGET_TYPES, ELEMENT_DND_TYPE } from '../../../common/elements/elementConstants';
import { NATIVE_TYPES } from '../../utils/dnd/dragAndDropUtils';

// Style
import './BoardDropTarget.scss';

type BoardDropTargetElement = Partial<MNElement> & Pick<MNElement, 'elementType'>;

type BoardDragElement = Partial<MNElement> & { elementType?: ElementType };

export type BoardDragItem = {
    draggedElementIds?: string[];
    draggedElements?: BoardDragElement[];
    unscaledElementOffsetsMap?: Record<string, { x: number; y: number; width: number; height: number }>;
    files?: Array<{ name: string }>;
    text?: string;
    urls?: string[];
};

type BoardDropMonitor = {
    getItem: () => BoardDragItem;
    getItemType: () => string | symbol | null;
    getDropResult: () => unknown;
    getClientOffset: () => { x: number; y: number } | null;
};

type BoardDropTargetProps = {
    element: BoardDropTargetElement;
    dispatchGetChildIds: (elementId: string) => string[];
    dispatchGetCanvasChildIds: (elementId: string) => string[];
    gridSize: number;
    connectDropTarget?: (element: HTMLDivElement | null) => void;
    isHovered?: boolean;
    setIsHovered: (isHovered: boolean | undefined) => void;
    setParentHoveredChildAcceptsDrop?: (acceptsDrop: boolean) => void;
};

/**
 * The full prop bag is forwarded to the drop location fns, which read more of it than this module names.
 */
type BoardDropLocationProps = {
    element: BoardDropTargetElement;
    dispatchGetCanvasChildIds: (elementId: string) => string[];
    [key: string]: unknown;
};

type BoardDropResult = {
    acceptedElementIds?: string[];
    location?: MNElementLocation;
};

/**
 * Prevent boards in columns from being able to drop on themselves.
 */
const dontDropOnSelfPredicate = ({
    props,
    monitor,
}: {
    props?: Pick<BoardDropTargetProps, 'element'>;
    monitor?: Pick<BoardDropMonitor, 'getItem'>;
}): boolean => {
    const { element } = props || {};
    const { draggedElementIds } = monitor?.getItem() || {};

    if (!draggedElementIds?.length) return true;

    const actualElementId = getElementId(element);
    const physicalElementId = getPhysicalId(element);

    return !draggedElementIds.includes(actualElementId) && !draggedElementIds.includes(physicalElementId);
};

/**
 * Routes an accepted board drop to the target board Canvas or Inbox.
 */
export const getBoardDropLocation = (
    props: BoardDropLocationProps,
    monitor: BoardDropMonitor,
    domNode?: HTMLElement | null,
): BoardDropResult => {
    const { element, dispatchGetCanvasChildIds } = props;

    const itemType = monitor.getItemType();
    const canvasChildIds = dispatchGetCanvasChildIds(getPhysicalId(element));
    const isEmptyCanvas = isEmpty(canvasChildIds);

    // Preserve a group’s layout when it is the first content added to a board's canvas.
    // The draggedElementIds? is empty when user drags files, urls or text onto the board.
    // In those scenarios, files, urls or text is available instead of draggedElementIds.
    const isMultiElementDrag = (monitor.getItem().draggedElementIds?.length ?? 0) > 1;

    if (isEmptyCanvas && itemType === ELEMENT_DND_TYPE && isMultiElementDrag) {
        return canvasDropLocationFn(props, monitor, domNode);
    }

    return inboxDropLocationFn(props, monitor, domNode);
};

const dropTargetConfig = {
    drop: getBoardDropLocation,
    canDrop: makeCanDropFn([
        preventSnappedDragDrop,
        preventAllElementPredicate([isLine, isAnnotation]),
        validElementPermissionsPredicate,
        dontDropOnSelfPredicate,
    ]),
    acceptDropTypes: NATIVE_TYPES,
    hoverType: DROP_TARGET_TYPES.BOARD,
};

const enhance = compose<BoardDropTargetProps, BoardDropTargetProps>(
    // Prevent the drop target from being added on drag.
    onlyShowIfNotPresentational,

    // Element Drop Target
    ElementDropTarget(dropTargetConfig),
    navigateOnHoverDecorator,
);

const BoardDropTarget: React.FC<BoardDropTargetProps> = (props) => {
    const { connectDropTarget, isHovered, setIsHovered, setParentHoveredChildAcceptsDrop } = props;

    useEffect(() => {
        setIsHovered(isHovered);

        if (setParentHoveredChildAcceptsDrop && isHovered) {
            setParentHoveredChildAcceptsDrop(true);
        }
    }, [isHovered]);

    return <div ref={connectDropTarget} className="BoardDropTarget" />;
};

export default enhance(BoardDropTarget);
